import { useCallback, useState } from 'react';
import { mindtimeWazuhEndpointsApi } from 'src/api/mindtime/wazuhEndpointsApi';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import type { WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import type { WazuhEndpointItem } from 'src/types/mindtime/wazuhEndpoints';
import logger from 'src/utils/logger';
import { getPreviewEndpointFilterOptions } from '../preview/endpoints';
import type { SecuritySearchableFilterOption } from './SecuritySearchableFilter';
import { formatEndpointLabel, readHostName } from './endpointLabel';

const resolveEndpointId = (item: WazuhEndpointItem): string =>
  String(item.wazuhAgentId ?? item.agentId ?? item.id ?? '');

const resolveEndpointLabel = (item: WazuhEndpointItem): string => {
  const endpoint = String(item.endpoint ?? item.agentName ?? item.name ?? resolveEndpointId(item));
  const hostName = readHostName(item);
  return hostName ? `${endpoint} (${formatEndpointLabel(hostName)})` : endpoint;
};

const toPreviewEndpointOptions = (): SecuritySearchableFilterOption[] =>
  getPreviewEndpointFilterOptions().map((option) => ({
    value: option.id,
    label: option.label,
  }));

export const useWazuhEndpointFilterOptions = (listScope: WazuhScopeParams) => {
  const { isPreviewMode } = useEdrDataGate('endpoints');
  const [options, setOptions] = useState<SecuritySearchableFilterOption[]>([]);
  const [loading, setLoading] = useState(false);

  const onSearch = useCallback(
    async (query: string) => {
      if (isPreviewMode) {
        const q = query.trim().toLowerCase();
        const all = toPreviewEndpointOptions();
        setOptions(
          q ? all.filter((option) => option.label.toLowerCase().includes(q)) : all
        );
        return;
      }

      setLoading(true);
      try {
        const result = await mindtimeWazuhEndpointsApi.list({
          ...listScope,
          search: query.trim() || undefined,
          page: 0,
          pageSize: 25,
        });
        setOptions(
          result.items
            .map((item) => ({
              value: resolveEndpointId(item),
              label: resolveEndpointLabel(item),
            }))
            .filter((option) => option.value)
        );
      } catch (error) {
        logger(error, 'error', 'useWazuhEndpointFilterOptions');
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [listScope, isPreviewMode]
  );

  return { options, loading, onSearch };
};
